import { useEffect, useMemo, useState } from 'react';
import { useOpenSupportRequests, useOpsPage, useSupportRequestList } from '../hooks/useDailyInstallations';
import { useDateModeRange } from '../components/OpsRecordList';
import {
  BRAND, BarRow, DayChart, Note, PageHeader, Panel, States, Stat,
  num, pct, selectClass, thisMonth,
} from '../components/OpsPageKit';

// DD/MM/YYYY and HH:mm, UK time - the format used everywhere in Nano.
const ukDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const ukTime = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })
    : '';
};
// The contracts are stored as BRSK / Netomnia, but the business calls them
// Region B / Region N, so that is what the page says.
const REGION_LABEL = { BRSK: 'Region B', Netomnia: 'Region N' };
const regionOf = (c) => REGION_LABEL[c] || c || '—';


function Decision({ value }) {
  const v = String(value || '').trim();
  const up = v.toUpperCase();
  const cls = up === 'APPROVED' ? 'bg-emerald-50 text-emerald-700'
    : up === 'REJECTED' ? 'bg-rose-50 text-rose-700' : 'bg-gray-100 text-gray-500';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{v || 'No decision'}</span>;
}

// How long a request has been waiting, from when it was raised.
const waited = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 60) return `${m}m`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
};

// MEWP and Supervisor requests nobody has actioned yet (no decision), last 7
// days, oldest first - the ones someone is still waiting on. The same list MEWP
// drivers and supervisors see for their own region in FSM.
function OpenNeedsAction({ contract }) {
  const { data, isLoading, isError } = useOpenSupportRequests(contract);
  const rows = data?.rows || [];
  const groups = ['MEWP', 'Supervisor'].map((type) => ({ type, rows: rows.filter((r) => r.request_type === type) }));
  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-2">
      {groups.map((g) => (
        <Panel key={g.type} title={`Open ${g.type} requests — needs action`}
          right={<span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${g.rows.length ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>{g.rows.length}</span>}>
          {isLoading && <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>}
          {isError && <p className="px-4 py-4 text-sm text-rose-600">Could not load open requests.</p>}
          {data && !g.rows.length && (
            <p className="px-4 py-6 text-center text-sm text-gray-400">{`Nothing waiting — every ${g.type} request from the last 7 days has a decision.`}</p>
          )}
          {g.rows.length ? (
            <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
              {g.rows.map((r) => (
                <li key={r.id} className="flex items-start justify-between gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{r.engineer_name || 'Unknown engineer'}</div>
                    <div className="truncate text-xs text-gray-500">
                      {[regionOf(r.contract), r.region, r.address].filter(Boolean).join(' · ')}
                    </div>
                    {(r.mewp_description || r.supervisor_request || r.comments) ? (
                      <div className="text-xs text-gray-400">{[r.mewp_description, r.supervisor_request, r.comments].filter(Boolean).join(' · ')}</div>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs font-semibold tabular-nums text-amber-700">{waited(r.time_requested) || '—'}</div>
                    <div className="text-[11px] text-gray-400">{`${ukDate(r.request_date)} ${ukTime(r.time_requested)}`}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </Panel>
      ))}
    </div>
  );
}

// Every individual request for any date range, one engineer, or a search -
// "did anyone raise one on this job / on that day". The panels above are the
// month's totals; this is the rows behind them.
function RequestList({ contract }) {
  // The Daily / Range toggle shared with the other ops lists.
  const { from, to, bad: badRange, control: dateControl } = useDateModeRange();
  const [engineer, setEngineer] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = useMemo(() => ({
    from, to, contract, engineer, q,
  }), [from, to, contract, engineer, q]);
  const { data, isLoading, isError, isFetching } = useSupportRequestList(badRange ? null : filters);

  const rows = data?.rows || [];
  const engineers = data?.engineers || [];
  // Keep the chosen name selectable even if the new range has none for them.
  const names = engineer && !engineers.some((e) => e.name === engineer)
    ? [{ name: engineer, n: 0 }, ...engineers] : engineers;

  return (
    <div className="mt-6">
      <Panel title="Requests">
        <div className="flex flex-wrap items-end gap-2 border-b border-gray-100 px-4 pb-3 pt-1">
          {dateControl}
          <label className="text-xs text-gray-500">
            Engineer
            <select value={engineer} onChange={(e) => setEngineer(e.target.value)}
              className={`mt-0.5 block max-w-[14rem] ${selectClass}`}>
              <option value="">Everyone</option>
              {names.map((e) => <option key={e.name} value={e.name}>{`${e.name} (${num(e.n)})`}</option>)}
            </select>
          </label>
          <label className="min-w-[12rem] flex-1 text-xs text-gray-500">
            Search
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Address, CBT number, type, reason, name…"
              className={`mt-0.5 block w-full ${selectClass}`} />
          </label>
          {(engineer || search) ? (
            <button type="button" onClick={() => { setEngineer(''); setSearch(''); }}
              className="pb-1.5 text-xs font-medium text-brand hover:underline">
              Clear
            </button>
          ) : null}
        </div>

        <div className="px-4 py-2 text-xs text-gray-500">
          {badRange && <span className="text-rose-600">The From date is after the To date.</span>}
          {!badRange && isLoading && 'Loading…'}
          {!badRange && isError && <span className="text-rose-600">Could not load the requests.</span>}
          {!badRange && data && (
            <>
              <strong className="text-gray-800">{num(data.total)}</strong>
              {` request${data.total === 1 ? '' : 's'} from ${ukDate(data.from)} to ${ukDate(data.to)}`}
              {data.total > rows.length ? ` — showing the latest ${num(rows.length)}; narrow the dates or search to see the rest` : ''}
              {isFetching ? ' · updating…' : ''}
            </>
          )}
        </div>

        {!badRange && rows.length ? (
          <div className="max-h-[36rem] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Engineer</th>
                  <th className="px-3 py-2">Region</th>
                  <th className="px-3 py-2">Area</th>
                  <th className="px-3 py-2">Request</th>
                  <th className="px-3 py-2">Address / CBT</th>
                  <th className="px-3 py-2">Decision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="align-top hover:bg-gray-50/60">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-800">
                      {ukDate(r.request_date)}
                      <div className="text-xs text-gray-400">{ukTime(r.time_requested)}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-800">{r.engineer_name || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-gray-600">{regionOf(r.contract)}</td>
                    <td className="px-3 py-2 text-gray-600">{r.region || '—'}</td>
                    <td className="max-w-[16rem] px-3 py-2">
                      <div className="text-gray-800">{r.request_type || '—'}</div>
                      {(r.mewp_description || r.comments) ? (
                        <div className="text-xs text-gray-500">{[r.mewp_description, r.comments].filter(Boolean).join(' · ')}</div>
                      ) : null}
                    </td>
                    <td className="max-w-[16rem] px-3 py-2 text-gray-600">
                      {r.address || '—'}
                      {r.cbt_number ? <div className="text-xs text-gray-400">{`CBT ${r.cbt_number}`}</div> : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Decision value={r.decision} />
                      {(r.approver || r.time_approved) ? (
                        <div className="mt-0.5 text-xs text-gray-400">
                          {[r.approver, ukTime(r.time_approved)].filter(Boolean).join(' · ')}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (!badRange && data ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">No support requests match.</p>
        ) : null)}
      </Panel>
    </div>
  );
}

// Support Request — the NEO2 rebuild.
//
// Volume, the approve/reject split, what engineers are asking for, and who is
// answering. The approver ranking covers Netomnia only; the page says so rather
// than presenting a partial league table as a whole one.
function SupportRequests() {
  const [month, setMonth] = useState(thisMonth());
  const [contract, setContract] = useState('');

  const filters = useMemo(() => ({ month, contract }), [month, contract]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('support-requests', filters);

  const t = data?.totals || {};
  const c = data?.caveats || {};
  const maxType = Math.max(1, ...(data?.types || []).map((r) => r.n));
  const maxReason = Math.max(1, ...(data?.reasons || []).map((r) => r.n));
  const maxApprover = Math.max(1, ...(data?.approvers || []).map((r) => r.approved));

  return (
    <div className="p-6">
      <PageHeader
        title="Support Requests"
        subtitle="What the field asked for, what was approved, and who answered."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={contract} onChange={(e) => setContract(e.target.value)} className={selectClass}>
          <option value="">Both regions</option>
          <option value="BRSK">Region B</option>
          <option value="Netomnia">Region N</option>
        </select>
      </PageHeader>

      <OpenNeedsAction contract={contract} />

      <States isLoading={isLoading} isError={isError} what="support requests" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Requests" value={num(t.total)} sub="in the month" accent />
            <Stat label="Approved" value={num(t.approved)} sub={pct(t.approval_rate)} tone="#16A34A" />
            <Stat label="Rejected" value={num(t.rejected)} tone={t.rejected ? '#DC2626' : undefined} />
            <Stat label="No decision" value={num(t.undecided)} sub="still open or blank" />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Requests by day">
              <DayChart
                rows={data.by_day}
                series={[
                  { key: 'approved', label: 'Approved', colour: BRAND },
                  { key: 'n', label: 'All requests', colour: '#E9D8F0' },
                ]}
              />
            </Panel>

            <Panel title="What was asked for">
              <div className="py-1">
                {data.types.map((r) => (
                  <BarRow
                    key={r.type}
                    label={r.type}
                    value={r.n}
                    total={maxType}
                    sub={`${num(r.approved)} approved`}
                  />
                ))}
              </div>
            </Panel>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Top reasons given">
              <div className="py-1">
                {data.reasons.slice(0, 10).map((r) => (
                  <BarRow key={r.reason} label={r.reason} value={r.n} total={maxReason} colour="#7C3AED" />
                ))}
              </div>
            </Panel>

            <Panel title="Approvers">
              {data.approvers.length ? (
                <div className="py-1">
                  {data.approvers.map((r) => (
                    <BarRow
                      key={r.approver}
                      label={r.approver}
                      value={r.approved}
                      total={maxApprover}
                      sub={`of ${num(r.n)} handled`}
                      colour="#16A34A"
                    />
                  ))}
                </div>
              ) : (
                <p className="px-4 py-6 text-center text-sm text-gray-400">
                  No approver recorded on any request this month.
                </p>
              )}
            </Panel>
          </div>

          <Note>
            <p>
              <strong>The approver ranking covers Region N only.</strong>{' '}
              The Region B list has an Approved by column that is never filled in.
            </p>
            {(c.by_list || []).length ? (
              <p className="mt-1">
                This month: {c.by_list.map((l) => `Region ${l.list.replace('support_request_', '').toUpperCase()} ${num(l.n)} requests, ${num(l.with_approver)} with an approver`).join('; ')}.
              </p>
            ) : null}
          </Note>
        </>
      )}

      <RequestList contract={contract} />
    </div>
  );
}

export default SupportRequests;
